import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemIcon,
    Menu,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Switch,
    FormControlLabel,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import { AddCircleOutline, ArrowBack, ArrowDropDown, ArrowDownward, ArrowUpward, Delete, Edit, Kitchen, Settings, Add } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { useFetchCategorias } from "../../hooks/useFetchCategorias";
import { useCadastrarEditarProdutoContext } from "../../contexts/CadastrarEditarProdutoContext";
import { IProduto } from "../../interfaces/IProduto";
import { IItemCombo } from "../../interfaces/IItemCombo";
import { IInsumo } from "../../interfaces/IInsumo";
import { TipoProduto } from "../../enum/TipoProduto";
import { TipoControleEstoque } from "../../enum/TipoControleEstoque";
import { ModeloPrecificacaoPizza, ModeloPrecificacaoPizzaDescricao } from "../../enum/ModeloPrecificacaoPizza";
import { IConfiguracaoProduto } from "../../interfaces/IConfiguracaoPizza";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useLetras } from "../../../core/hooks/useLetras";
import { IImagemProduto } from "../../interfaces/IImagemProduto";
import { CardUploadImagem } from "../../../core/components/CardUploadImagem";
import { ModalAtualizaPreco } from "../ModalAtualizarPreco";
import { MIMETypes } from "../../../core/enum/MIMETypes";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { ButtonsContainerForm } from "../ConfiguracaoPreparadoContainer/styles";
import { FichaTecnica } from "../FichaTecnica";
import { FormularioImposto } from "../ConfiguracaoImpostoContainer/FormularioImposto";
import { ProcessoPreparoForm } from "../../../cozinha/components/ProcessoPreparoForm";
import { useEstacoesKDS } from "../../../cozinha/hooks/useEstacoesKDS";

// ─── types ───────────────────────────────────────────────────────────────────

type ViewMode = "slots" | "slot-items" | "item-form";

type SubItem = {
    uuid: string;
    descricao: string;
    detalhes?: string;
    preco: number;
    precoSemDesconto?: number;
    imagens?: IImagemProduto[];
};

type SubSection = {
    id: string;
    label: string;
    items: SubItem[];
};

// abas fixas do editor de um produto do combo (além das seções de sub-itens)
const TAB_DADOS = "__dados";
const TAB_KDS = "__kds";
const TAB_ESTOQUE = "__estoque";
const TAB_FISCAL = "__fiscal";

// ─── helpers ─────────────────────────────────────────────────────────────────

const TIPOS_COM_COMPLEMENTO = [TipoProduto.pizza, TipoProduto.preparado];

const labelTipo = (tipo: TipoProduto) => {
    const mapa: Record<string, string> = { pizza: "Pizza", preparado: "Preparado", industrializado: "Industrializado", peso: "Peso", combo: "Combo" };
    return mapa[tipo] ?? tipo;
};

function criarProdutoEditavel(produto: IProduto, herdar: boolean): IProduto {
    return {
        ...produto,
        // Cada item do combo é uma cópia própria (produto oculto "apenas combo") que referencia o original.
        // uuid próprio garante KDS/estoque editáveis por item; produtoOrigemUuid mantém o rastreio no original.
        uuid: v4(),
        apenasCombo: true,
        produtoOrigemUuid: produto.produtoOrigemUuid ?? produto.uuid,
        // Nome do produto original (capturado antes de qualquer renomeação), usado no lançamento financeiro.
        descricaoOrigem: produto.descricaoOrigem ?? produto.descricao,
        sabores: produto.sabores?.map(s => ({ ...s, preco: herdar ? (s.preco ?? 0) : 0, precoSemDesconto: herdar ? s.precoSemDesconto : undefined })),
        bordas: produto.bordas?.map(b => ({ ...b, preco: herdar ? (b.preco ?? 0) : 0, precoSemDesconto: herdar ? b.precoSemDesconto : undefined })),
        massas: produto.massas?.map(m => ({ ...m, preco: herdar ? (m.preco ?? 0) : 0, precoSemDesconto: herdar ? m.precoSemDesconto : undefined })),
        adicionais: produto.adicionais?.map(a => ({ ...a, preco: herdar ? (a.preco ?? 0) : 0, precoSemDesconto: herdar ? a.precoSemDesconto : undefined })),
        complementos: produto.complementos?.map(c => ({
            ...c,
            itens: c.itens.map(it => ({ ...it, preco: herdar ? (it.preco ?? 0) : 0, precoSemDesconto: herdar ? it.precoSemDesconto : undefined })),
        })),
    };
}

// produto totalmente novo, criado do zero dentro do combo (não existe no cardápio)
function criarProdutoNovo(descricao: string): IProduto {
    const novoUuid = v4();
    return {
        uuid: novoUuid,
        tipo: TipoProduto.preparado,
        descricao,
        preco: 0,
        apenasCombo: true,
        produtoOrigemUuid: novoUuid,
        complementos: [],
        imagens: [],
    };
}

function buildSubSections(produto: IProduto): SubSection[] {
    if (produto.tipo === TipoProduto.pizza) {
        const sections: SubSection[] = [];
        if (produto.sabores?.length) sections.push({ id: "sabores", label: "Sabores", items: produto.sabores.map(s => ({ uuid: s.uuid, descricao: s.descricao, detalhes: s.detalhes, preco: s.preco ?? 0, precoSemDesconto: s.precoSemDesconto, imagens: s.imagens })) });
        if (produto.bordas?.length) sections.push({ id: "bordas", label: "Bordas", items: produto.bordas.map(b => ({ uuid: b.uuid, descricao: b.descricao, preco: b.preco ?? 0, precoSemDesconto: b.precoSemDesconto, imagens: b.imagens })) });
        if (produto.massas?.length) sections.push({ id: "massas", label: "Massas", items: produto.massas.map(m => ({ uuid: m.uuid, descricao: m.descricao, preco: m.preco ?? 0, precoSemDesconto: m.precoSemDesconto, imagens: m.imagens })) });
        if (produto.adicionais?.length) sections.push({ id: "adicionais", label: "Adicionais", items: produto.adicionais.map(a => ({ uuid: a.uuid, descricao: a.descricao, detalhes: a.detalhes, preco: a.preco ?? 0, precoSemDesconto: a.precoSemDesconto, imagens: a.imagens })) });
        return sections;
    }
    if (produto.tipo === TipoProduto.preparado) {
        return (produto.complementos ?? []).map(c => ({ id: c.uuid, label: c.descricao, items: c.itens.map(it => ({ uuid: it.uuid, descricao: it.descricao, detalhes: it.detalhes, preco: it.preco ?? 0, precoSemDesconto: it.precoSemDesconto, imagens: it.imagens })) }));
    }
    return [];
}

function applySubItemUpdate(produto: IProduto, sectionId: string, item: SubItem): IProduto {
    if (produto.tipo === TipoProduto.pizza) {
        const field = sectionId as "sabores" | "bordas" | "massas" | "adicionais";
        return {
            ...produto,
            [field]: (produto[field] as any[])?.map((s: any) =>
                s.uuid === item.uuid ? { ...s, descricao: item.descricao, detalhes: item.detalhes, preco: item.preco, precoSemDesconto: item.precoSemDesconto, imagens: item.imagens } : s
            ),
        };
    }
    if (produto.tipo === TipoProduto.preparado) {
        return {
            ...produto,
            complementos: produto.complementos?.map(c =>
                c.uuid === sectionId
                    ? { ...c, itens: c.itens.map(it => it.uuid === item.uuid ? { ...it, descricao: item.descricao, detalhes: item.detalhes, preco: item.preco, precoSemDesconto: item.precoSemDesconto, imagens: item.imagens } : it) }
                    : c
            ),
        };
    }
    return produto;
}

function applyBulkPriceUpdate(produto: IProduto, sectionId: string, itemUuids: string[], preco: number, precoSemDesconto?: number): IProduto {
    if (produto.tipo === TipoProduto.pizza) {
        const field = sectionId as "sabores" | "bordas" | "massas" | "adicionais";
        return {
            ...produto,
            [field]: (produto[field] as any[])?.map((s: any) =>
                itemUuids.includes(s.uuid) ? { ...s, preco, precoSemDesconto: precoSemDesconto ?? s.precoSemDesconto } : s
            ),
        };
    }
    if (produto.tipo === TipoProduto.preparado) {
        return {
            ...produto,
            complementos: produto.complementos?.map(c =>
                c.uuid === sectionId
                    ? { ...c, itens: c.itens.map(it => itemUuids.includes(it.uuid) ? { ...it, preco, precoSemDesconto: precoSemDesconto ?? it.precoSemDesconto } : it) }
                    : c
            ),
        };
    }
    return produto;
}

function addSubItem(produto: IProduto, sectionId: string, item: SubItem): IProduto {
    const novo: any = {
        uuid: item.uuid,
        descricao: item.descricao,
        detalhes: item.detalhes,
        preco: item.preco,
        precoSemDesconto: item.precoSemDesconto,
        imagens: item.imagens ?? [],
        quantidade: 1,
    };
    if (produto.tipo === TipoProduto.pizza) {
        const field = sectionId as "sabores" | "bordas" | "massas" | "adicionais";
        return { ...produto, [field]: [ ...((produto[field] as any[]) ?? []), novo ] };
    }
    if (produto.tipo === TipoProduto.preparado) {
        return {
            ...produto,
            complementos: produto.complementos?.map(c => c.uuid === sectionId ? { ...c, itens: [...c.itens, novo] } : c),
        };
    }
    return produto;
}

function removeSubItems(produto: IProduto, sectionId: string, uuids: string[]): IProduto {
    if (produto.tipo === TipoProduto.pizza) {
        const field = sectionId as "sabores" | "bordas" | "massas" | "adicionais";
        return { ...produto, [field]: (produto[field] as any[])?.filter((s: any) => !uuids.includes(s.uuid)) };
    }
    if (produto.tipo === TipoProduto.preparado) {
        return {
            ...produto,
            complementos: produto.complementos?.map(c => c.uuid === sectionId ? { ...c, itens: c.itens.filter(it => !uuids.includes(it.uuid)) } : c),
        };
    }
    return produto;
}

function addComplementoGroup(produto: IProduto, descricao: string): IProduto {
    return {
        ...produto,
        complementos: [
            ...(produto.complementos ?? []),
            { uuid: v4(), descricao, obrigatorio: false, numeroMinimoItens: 0, numeroMaximoItens: 1, itens: [] },
        ],
    };
}

// ─── main component ───────────────────────────────────────────────────────────

export const ConfiguracaoProdutosComboContainer = () => {
    const { categorias, isLoading } = useFetchCategorias();
    const { itensCombo, adicionarItemCombo, editarItemCombo, removerItemCombo, trocarPosicaoItemCombo, handleVoltar, kdsDesabilitado, kdsSimples } =
        useCadastrarEditarProdutoContext();
    const { real, number } = useMoeda();
    const { titulo } = useLetras();
    const snackbar = useSnackbar();
    const { estacoes } = useEstacoesKDS();

    // ── navigation ──
    const [viewMode, setViewMode] = useState<ViewMode>("slots");
    const [selectedSlotUuid, setSelectedSlotUuid] = useState<string | null>(null);
    const [slotTab, setSlotTab] = useState<string>(TAB_DADOS);

    // ── slot table state ──
    const [slotsSelecionados, setSlotsSelecionados] = useState<string[]>([]);
    const [anchorSlots, setAnchorSlots] = useState<null | HTMLElement>(null);

    // ── sub-items table state ──
    const [itensSelecionados, setItensSelecionados] = useState<string[]>([]);
    const [anchorItens, setAnchorItens] = useState<null | HTMLElement>(null);
    const [atualizacaoPreco, setAtualizacaoPreco] = useState(false);

    // ── item form state ──
    const [itemEmEdicao, setItemEmEdicao] = useState<SubItem | null>(null);
    const [criandoItem, setCriandoItem] = useState(false);
    const [formDescricao, setFormDescricao] = useState("");
    const [formDetalhes, setFormDetalhes] = useState("");
    const [formPreco, setFormPreco] = useState("");
    const [formPrecoSemDesconto, setFormPrecoSemDesconto] = useState("");
    const [formImagem, setFormImagem] = useState<File | IImagemProduto | undefined>(undefined);

    // ── add-product dialogs ──
    const [produtoSelecionado, setProdutoSelecionado] = useState<IProduto | null>(null);
    const [pendingProduto, setPendingProduto] = useState<IProduto | null>(null);
    const [pendingPreco, setPendingPreco] = useState(0);
    const [dialogPrecoOpen, setDialogPrecoOpen] = useState(false);
    const [dialogComplementoOpen, setDialogComplementoOpen] = useState(false);

    // ── novo produto dialog ──
    const [dialogNovoOpen, setDialogNovoOpen] = useState(false);
    const [novoDescricao, setNovoDescricao] = useState("");

    // ── novo grupo de complemento dialog ──
    const [dialogGrupoOpen, setDialogGrupoOpen] = useState(false);
    const [grupoDescricao, setGrupoDescricao] = useState("");

    const todosProdutos: IProduto[] = (categorias ?? []).flatMap(cat => cat.produtos ?? []).filter(p => !p.apenasCombo);
    const selectedSlot = itensCombo.find(i => i.uuid === selectedSlotUuid) ?? null;
    const subSections = selectedSlot ? buildSubSections(selectedSlot.produto) : [];
    const currentSection = subSections.find(s => s.id === slotTab) ?? null;

    // reset aba ao trocar de slot
    useEffect(() => { setSlotTab(TAB_DADOS); setItensSelecionados([]); }, [selectedSlotUuid]);

    // Backfill do nome de origem: itens copiados sem descricaoOrigem (ex.: combos antigos) têm o nome
    // original resolvido pelo produtoOrigemUuid a partir do cardápio. Assim, reabrir/salvar o combo
    // garante o nome correto no lançamento financeiro, sem precisar recriar o item.
    useEffect(() => {
        if (!categorias?.length || !itensCombo.length) return;
        itensCombo.forEach(item => {
            const p = item.produto;
            if (!p.descricaoOrigem && p.produtoOrigemUuid && p.produtoOrigemUuid !== p.uuid) {
                const original = categorias.flatMap(c => c.produtos ?? []).find(o => o.uuid === p.produtoOrigemUuid);
                if (original?.descricao) {
                    editarItemCombo(item.uuid, { produto: { ...p, descricaoOrigem: original.descricao } });
                }
            }
        });
    }, [categorias, itensCombo]);

    // ── atualização do produto filho ──
    const setChildProduto = (patch: Partial<IProduto>) => {
        if (!selectedSlot) return;
        editarItemCombo(selectedSlot.uuid, { produto: { ...selectedSlot.produto, ...patch } });
    };

    const setConfig = (patch: Partial<IConfiguracaoProduto>) => {
        if (!selectedSlot) return;
        setChildProduto({ configuracao: { ...(selectedSlot.produto.configuracao ?? {}), ...patch } });
    };

    // ── slot navigation ──
    const irParaSlotItems = (slotUuid: string) => {
        setSelectedSlotUuid(slotUuid);
        setViewMode("slot-items");
        setSlotsSelecionados([]);
    };

    const voltarParaSlots = () => {
        setViewMode("slots");
        setSelectedSlotUuid(null);
        setItensSelecionados([]);
    };

    const irParaItemForm = (item: SubItem) => {
        setItemEmEdicao(item);
        setFormDescricao(item.descricao);
        setFormDetalhes(item.detalhes ?? "");
        setFormPreco(String(item.preco));
        setFormPrecoSemDesconto(item.precoSemDesconto ? String(item.precoSemDesconto) : "");
        setFormImagem(item.imagens?.[0]);
        setViewMode("item-form");
        setAnchorItens(null);
    };

    const voltarParaSlotItems = () => {
        setViewMode("slot-items");
        setItemEmEdicao(null);
        setCriandoItem(false);
        setItensSelecionados([]);
    };

    const irParaNovoItem = () => {
        if (!currentSection) return;
        const novo: SubItem = { uuid: v4(), descricao: "", preco: 0 };
        setItemEmEdicao(novo);
        setCriandoItem(true);
        setFormDescricao("");
        setFormDetalhes("");
        setFormPreco("0");
        setFormPrecoSemDesconto("");
        setFormImagem(undefined);
        setViewMode("item-form");
    };

    const handleRemoverItens = () => {
        if (!selectedSlot || !currentSection) return;
        const novoProduto = removeSubItems(selectedSlot.produto, currentSection.id, itensSelecionados);
        editarItemCombo(selectedSlot.uuid, { produto: novoProduto });
        setItensSelecionados([]);
        setAnchorItens(null);
    };

    const handleCriarGrupo = () => {
        if (!selectedSlot) return;
        const descricao = grupoDescricao.trim();
        if (!descricao) return snackbar("Informe o nome do grupo", { severity: "error" });
        const novoProduto = addComplementoGroup(selectedSlot.produto, descricao);
        editarItemCombo(selectedSlot.uuid, { produto: novoProduto });
        const novoGrupo = novoProduto.complementos?.[novoProduto.complementos.length - 1];
        setGrupoDescricao("");
        setDialogGrupoOpen(false);
        if (novoGrupo) setSlotTab(novoGrupo.uuid);
    };

    const handleRemoverGrupo = () => {
        if (!selectedSlot || !currentSection) return;
        const novoProduto = {
            ...selectedSlot.produto,
            complementos: (selectedSlot.produto.complementos ?? []).filter(c => c.uuid !== currentSection.id),
        };
        editarItemCombo(selectedSlot.uuid, { produto: novoProduto });
        setItensSelecionados([]);
        setSlotTab(TAB_DADOS);
    };

    // ── slot table actions ──
    const toggleSlot = (uuid: string) => setSlotsSelecionados(prev => prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]);
    const toggleTodosSlots = () => setSlotsSelecionados(slotsSelecionados.length === itensCombo.length ? [] : itensCombo.map(i => i.uuid));

    const handleExcluirSlotsSelecionados = () => {
        slotsSelecionados.forEach(uuid => removerItemCombo(uuid));
        setSlotsSelecionados([]);
        setAnchorSlots(null);
    };

    // ── sub-items table actions ──
    const toggleItem = (uuid: string) => setItensSelecionados(prev => prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]);
    const toggleTodosItens = () => {
        if (!currentSection) return;
        setItensSelecionados(itensSelecionados.length === currentSection.items.length ? [] : currentSection.items.map(i => i.uuid));
    };

    const handleAtualizarPrecoEmMassa = (preco: string, precoSemDesconto?: string) => {
        if (!selectedSlot || !currentSection) return;
        const novoProduto = applyBulkPriceUpdate(selectedSlot.produto, currentSection.id, itensSelecionados, number(preco), precoSemDesconto ? number(precoSemDesconto) : undefined);
        editarItemCombo(selectedSlot.uuid, { produto: novoProduto });
        setItensSelecionados([]);
        setAtualizacaoPreco(false);
    };

    // ── item form save ──
    const handleSalvarItem = () => {
        if (!selectedSlot || !currentSection || !itemEmEdicao) return;
        const imagens: IImagemProduto[] = formImagem ? [formImagem as IImagemProduto] : [];
        const updated: SubItem = { ...itemEmEdicao, descricao: formDescricao, detalhes: formDetalhes || undefined, preco: number(formPreco), precoSemDesconto: formPrecoSemDesconto ? number(formPrecoSemDesconto) : undefined, imagens };
        const novoProduto = criandoItem
            ? addSubItem(selectedSlot.produto, currentSection.id, updated)
            : applySubItemUpdate(selectedSlot.produto, currentSection.id, updated);
        editarItemCombo(selectedSlot.uuid, { produto: novoProduto });
        voltarParaSlotItems();
    };

    // ── add product flow (copiar existente) ──
    const handleSelecionarProduto = (produto: IProduto | null) => {
        if (!produto) return;
        setProdutoSelecionado(null);
        setPendingProduto(produto);
        setPendingPreco(produto.preco ?? 0);
        setDialogPrecoOpen(true);
    };

    const handleDialogPrecoConfirm = (incluir: boolean) => {
        setDialogPrecoOpen(false);
        const preco = incluir ? (pendingProduto?.preco ?? 0) : 0;
        setPendingPreco(preco);
        if (TIPOS_COM_COMPLEMENTO.includes(pendingProduto?.tipo as TipoProduto)) {
            setDialogComplementoOpen(true);
        } else {
            finalizarAdicao(preco, false);
        }
    };

    const handleDialogComplementoConfirm = (herdar: boolean) => {
        setDialogComplementoOpen(false);
        finalizarAdicao(pendingPreco, herdar);
    };

    const finalizarAdicao = (preco: number, herdarPrecoComplementos: boolean) => {
        if (!pendingProduto) return;
        adicionarItemCombo({ uuid: v4(), quantidade: 1, preco, herdarPrecoComplementos, produto: criarProdutoEditavel(pendingProduto, herdarPrecoComplementos) });
        setPendingProduto(null);
    };

    // ── criar produto novo ──
    const handleCriarProdutoNovo = () => {
        const descricao = novoDescricao.trim();
        if (!descricao) return snackbar("Informe o nome do produto", { severity: "error" });
        const slotUuid = v4();
        adicionarItemCombo({ uuid: slotUuid, quantidade: 1, preco: 0, herdarPrecoComplementos: false, produto: criarProdutoNovo(descricao) });
        setNovoDescricao("");
        setDialogNovoOpen(false);
        irParaSlotItems(slotUuid);
    };

    const handleUploadImagem = (arquivo: File) => {
        const validos = [MIMETypes["image/jpeg"], MIMETypes["image/jpg"], MIMETypes["image/png"]] as string[];
        if (!validos.includes(arquivo.type)) return snackbar("Arquivo inválido (PNG, JPEG)", { severity: "error" });
        setFormImagem(arquivo);
    };

    // ── estoque (por produto do combo) ──
    const opcoesControleEstoque = [
        { label: "Desabilitado", value: undefined as TipoControleEstoque | undefined, disabled: false },
        ...Object.entries(TipoControleEstoque).map(([key, value]) => ({
            label: titulo(key),
            value: value as TipoControleEstoque,
            disabled:
                (value === TipoControleEstoque.completo && selectedSlot?.produto.tipo === TipoProduto.industrializado) ||
                (value === TipoControleEstoque.simplificado && selectedSlot?.produto.tipo === TipoProduto.pizza),
        })),
    ];

    const adicionarInsumoChild = (insumo: IInsumo) => {
        if (!selectedSlot) return;
        setChildProduto({ fichaTecnica: [...(selectedSlot.produto.fichaTecnica ?? []), insumo] });
    };
    const excluirInsumoChild = (insumoUuid: string) => {
        if (!selectedSlot) return;
        setChildProduto({ fichaTecnica: (selectedSlot.produto.fichaTecnica ?? []).filter(i => i.uuid !== insumoUuid) });
    };

    // ── KDS (por produto do combo) ──
    const produtoAtual = selectedSlot?.produto;
    const estacaoKdsUuid = produtoAtual?.configuracaoKDS?.estacaoKdsUuid ?? "";
    const tempoMinutos = produtoAtual?.configuracaoKDS?.tempoPreparoSegundos ? Math.round(produtoAtual.configuracaoKDS.tempoPreparoSegundos / 60) : 5;
    const processosKDS = produtoAtual?.processosPreparoKDS ?? [];

    const handleEstacao = (uuid: string) => {
        setChildProduto({ configuracaoKDS: uuid ? { estacaoKdsUuid: uuid, tempoPreparoSegundos: tempoMinutos * 60 } : undefined });
    };
    const handleTempo = (minutos: number) => {
        if (!estacaoKdsUuid) return;
        setChildProduto({ configuracaoKDS: { estacaoKdsUuid, tempoPreparoSegundos: minutos * 60 } });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // VIEW: item form (edição de um sub-item)
    // ─────────────────────────────────────────────────────────────────────────
    if (viewMode === "item-form" && itemEmEdicao) {
        return (
            <Paper sx={{ p: 2, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <IconButton size="small" onClick={voltarParaSlotItems}><ArrowBack /></IconButton>
                    <Typography variant="h6">{criandoItem ? "Novo item" : `Editar: ${itemEmEdicao.descricao}`}</Typography>
                </Box>
                <TextField fullWidth label="Nome" value={formDescricao} onChange={e => setFormDescricao(e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth multiline minRows={2} maxRows={12} label="Detalhes" value={formDetalhes} onChange={e => setFormDetalhes(e.target.value)} sx={{ mb: 2 }} />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Preço</InputLabel>
                    <OutlinedInput startAdornment={<InputAdornment position="start">R$</InputAdornment>} label="Preço" value={formPreco} onChange={e => setFormPreco(e.target.value)} />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Preço sem desconto</InputLabel>
                    <OutlinedInput startAdornment={<InputAdornment position="start">R$</InputAdornment>} label="Preço sem desconto" value={formPrecoSemDesconto} onChange={e => setFormPrecoSemDesconto(e.target.value)} />
                </FormControl>
                <CardUploadImagem
                    titulo="Imagem"
                    descricao={formImagem ? (formImagem instanceof File ? formImagem.name : (formImagem.nomeOriginal ?? formImagem.nome)) : "Faça o upload"}
                    url={formImagem instanceof File ? URL.createObjectURL(formImagem) : formImagem?.url}
                    excluir={() => setFormImagem(undefined)}
                    upload={handleUploadImagem}
                />
                <ButtonsContainerForm sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={voltarParaSlotItems}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvarItem} disabled={!formDescricao}>Salvar</Button>
                </ButtonsContainerForm>
            </Paper>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // VIEW: editor do produto do combo (dados, sub-itens, KDS, estoque)
    // ─────────────────────────────────────────────────────────────────────────
    if (viewMode === "slot-items" && selectedSlot && produtoAtual) {
        return (
            <Paper sx={{ p: 2, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <IconButton size="small" onClick={voltarParaSlots}><ArrowBack /></IconButton>
                    <Typography variant="h6">
                        {produtoAtual.descricao || "Produto do combo"}
                        <Chip label={labelTipo(produtoAtual.tipo)} size="small" variant="outlined" sx={{ ml: 1 }} />
                    </Typography>
                </Box>

                <Tabs value={slotTab} onChange={(_, v) => { setSlotTab(v); setItensSelecionados([]); }} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                    <Tab label="Dados" value={TAB_DADOS} />
                    {subSections.map(s => <Tab key={s.id} label={s.label} value={s.id} />)}
                    <Tab label="Cozinha (KDS)" value={TAB_KDS} />
                    <Tab label="Estoque" value={TAB_ESTOQUE} />
                    <Tab label="Fiscal" value={TAB_FISCAL} />
                </Tabs>

                {/* ── DADOS ── */}
                {slotTab === TAB_DADOS && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label="Nome do produto"
                            value={produtoAtual.descricao}
                            onChange={e => setChildProduto({ descricao: e.target.value })}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Preço no combo</InputLabel>
                            <OutlinedInput
                                startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                label="Preço no combo"
                                defaultValue={String(selectedSlot.preco)}
                                onBlur={e => { const v = number(e.target.value); if (!isNaN(v)) editarItemCombo(selectedSlot.uuid, { preco: v }); }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Valor deste item dentro do combo (0 para "incluso").
                            </Typography>
                        </FormControl>

                        {/* Configuração da pizza (por produto do combo) */}
                        {produtoAtual.tipo === TipoProduto.pizza && (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel>Modelo de precificação</InputLabel>
                                    <Select
                                        label="Modelo de precificação"
                                        value={produtoAtual.configuracao?.modeloPrecificacao ?? ""}
                                        onChange={e => setConfig({ modeloPrecificacao: e.target.value as ModeloPrecificacaoPizza })}
                                    >
                                        {Object.values(ModeloPrecificacaoPizza).map(v => (
                                            <MenuItem key={v} value={v}>{ModeloPrecificacaoPizzaDescricao[v]}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <TextField
                                        type="number"
                                        label="Nº de sabores"
                                        sx={{ flex: 1, minWidth: 140 }}
                                        value={produtoAtual.quantidadeSabores ?? ""}
                                        inputProps={{ min: 1 }}
                                        onChange={e => setChildProduto({ quantidadeSabores: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })}
                                    />
                                    <TextField
                                        type="number"
                                        label="Nº de fatias"
                                        sx={{ flex: 1, minWidth: 140 }}
                                        value={produtoAtual.quantidadeFatias ?? ""}
                                        inputProps={{ min: 1 }}
                                        onChange={e => setChildProduto({ quantidadeFatias: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })}
                                    />
                                    <TextField
                                        type="number"
                                        label="Máx. adicionais"
                                        sx={{ flex: 1, minWidth: 140 }}
                                        value={produtoAtual.configuracao?.maxAdicionais ?? ""}
                                        inputProps={{ min: 0 }}
                                        onChange={e => setConfig({ maxAdicionais: e.target.value ? Math.max(0, Number(e.target.value)) : undefined })}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <FormControlLabel
                                        control={<Switch checked={!!produtoAtual.configuracao?.massaObrigatoria} onChange={e => setConfig({ massaObrigatoria: e.target.checked })} />}
                                        label="Massa obrigatória"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={!!produtoAtual.configuracao?.bordaObrigatoria} onChange={e => setConfig({ bordaObrigatoria: e.target.checked })} />}
                                        label="Borda obrigatória"
                                    />
                                </Box>
                            </>
                        )}

                        {/* Configuração do preparado (por produto do combo) */}
                        {produtoAtual.tipo === TipoProduto.preparado && (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel>Preço base do produto</InputLabel>
                                    <OutlinedInput
                                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                        label="Preço base do produto"
                                        defaultValue={String(produtoAtual.preco ?? 0)}
                                        onBlur={e => { const v = number(e.target.value); if (!isNaN(v)) setChildProduto({ preco: v }); }}
                                    />
                                </FormControl>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <FormControlLabel
                                        control={<Switch checked={!!produtoAtual.configuracao?.habilitarComplementos} onChange={e => setConfig({ habilitarComplementos: e.target.checked })} />}
                                        label="Habilitar complementos"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={!!produtoAtual.configuracao?.habilitarObservacao} onChange={e => setConfig({ habilitarObservacao: e.target.checked })} />}
                                        label="Habilitar observação"
                                    />
                                </Box>
                                <Box>
                                    <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={() => { setGrupoDescricao(""); setDialogGrupoOpen(true); }}>
                                        Adicionar grupo de complemento
                                    </Button>
                                    {subSections.length === 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                            Crie grupos (ex.: "Sabores", "Acompanhamentos") e depois adicione os itens de cada grupo na aba correspondente.
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {/* ── SUB-ITENS (sabores/bordas/complementos...) ── */}
                {currentSection && (
                    <TableContainer variant="outlined" component={Paper} sx={{ maxHeight: 420 }}>
                        <Toolbar sx={{ justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="subtitle1">{currentSection.label}</Typography>
                                {produtoAtual.tipo === TipoProduto.preparado && (
                                    <Tooltip title="Remover este grupo de complemento">
                                        <Button color="error" size="small" startIcon={<Delete fontSize="small" />} onClick={handleRemoverGrupo}>
                                            Remover grupo
                                        </Button>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button variant="outlined" startIcon={<AddCircleOutline />} onClick={irParaNovoItem}>
                                    Adicionar
                                </Button>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowDropDown />}
                                    disabled={itensSelecionados.length === 0}
                                    onClick={e => setAnchorItens(e.currentTarget)}
                                >
                                    Ações
                                </Button>
                                <Menu anchorEl={anchorItens} open={Boolean(anchorItens)} onClose={() => setAnchorItens(null)}>
                                    <MenuItem disabled>
                                        <Typography variant="inherit" noWrap>{itensSelecionados.length} selecionado(s)</Typography>
                                    </MenuItem>
                                    <Divider />
                                    <Tooltip title={itensSelecionados.length === 1 ? "Editar" : "Alterar preço em massa"}>
                                        <MenuItem onClick={() => {
                                            if (itensSelecionados.length === 1) {
                                                const item = currentSection.items.find(i => i.uuid === itensSelecionados[0]);
                                                if (item) irParaItemForm(item);
                                            } else {
                                                setAtualizacaoPreco(true);
                                                setAnchorItens(null);
                                            }
                                        }}>
                                            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                                            <Typography variant="inherit" noWrap>{itensSelecionados.length === 1 ? "Editar" : "Alterar preço"}</Typography>
                                        </MenuItem>
                                    </Tooltip>
                                    <MenuItem onClick={handleRemoverItens}>
                                        <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Remover</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Toolbar>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <input type="checkbox"
                                            checked={itensSelecionados.length === currentSection.items.length && currentSection.items.length > 0}
                                            onChange={toggleTodosItens}
                                        />
                                    </TableCell>
                                    <TableCell>Imagem</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell align="right">Preço</TableCell>
                                    <TableCell align="right">Sem desconto</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentSection.items.map(item => (
                                    <TableRow key={item.uuid} hover sx={{ cursor: "pointer" }} onClick={() => toggleItem(item.uuid)}>
                                        <TableCell padding="checkbox">
                                            <input type="checkbox" checked={itensSelecionados.includes(item.uuid)} onChange={() => toggleItem(item.uuid)} onClick={e => e.stopPropagation()} />
                                        </TableCell>
                                        <TableCell>
                                            <img
                                                alt={item.descricao}
                                                src={item.imagens?.length ? (item.imagens[0] instanceof File ? URL.createObjectURL(item.imagens[0] as any) : item.imagens[0].url) : `${process.env.PUBLIC_URL}/images/produtos/generico.png`}
                                                style={{ width: 48, height: 40, borderRadius: 4, objectFit: "cover" }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontSize={14}>{item.descricao}</Typography>
                                            {item.detalhes && <Typography fontSize={11} color="text.secondary">{item.detalhes.length > 40 ? `${item.detalhes.slice(0, 40)}...` : item.detalhes}</Typography>}
                                        </TableCell>
                                        <TableCell align="right">{real(item.preco)}</TableCell>
                                        <TableCell align="right">{item.precoSemDesconto ? real(item.precoSemDesconto) : "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* ── KDS ── */}
                {slotTab === TAB_KDS && (
                    <Box>
                        {kdsDesabilitado ? (
                            <Alert severity="info">O KDS está desabilitado. Configure o modo em Cozinha &gt; Configurações.</Alert>
                        ) : kdsSimples ? (
                            <Alert severity="info">No modo KDS Simples, o roteamento é por estação. Não é necessário configurar KDS por produto.</Alert>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Kitchen color={estacaoKdsUuid || processosKDS.length > 0 ? "primary" : "disabled"} />
                                    <Typography variant="subtitle1">Integração KDS</Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                                    Estação padrão usada quando não há processos de preparo configurados abaixo.
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Estação KDS</InputLabel>
                                        <Select value={estacaoKdsUuid} label="Estação KDS" onChange={e => handleEstacao(e.target.value)}>
                                            <MenuItem value=""><em>Nenhuma</em></MenuItem>
                                            {estacoes.map(e => <MenuItem key={e.uuid} value={e.uuid}>{e.nome}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    {estacaoKdsUuid && (
                                        <TextField
                                            label="Tempo de preparo (min)"
                                            type="number"
                                            size="small"
                                            value={tempoMinutos}
                                            onChange={e => handleTempo(Math.max(1, Number(e.target.value)))}
                                            inputProps={{ min: 1, max: 120 }}
                                            fullWidth
                                        />
                                    )}
                                </Box>
                                <Divider sx={{ my: 3 }} />
                                <ProcessoPreparoForm
                                    processos={processosKDS}
                                    estacoes={estacoes}
                                    onAdicionar={async (dados) => setChildProduto({ processosPreparoKDS: [...processosKDS, { ...dados, uuid: v4() }] })}
                                    onAtualizar={async (uuid, dados) => setChildProduto({ processosPreparoKDS: processosKDS.map(p => p.uuid === uuid ? { ...p, ...dados } : p) })}
                                    onRemover={async (uuid) => setChildProduto({ processosPreparoKDS: processosKDS.filter(p => p.uuid !== uuid) })}
                                />
                            </>
                        )}
                    </Box>
                )}

                {/* ── ESTOQUE ── */}
                {slotTab === TAB_ESTOQUE && (
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Controle de estoque</Typography>
                        <Autocomplete
                            options={opcoesControleEstoque}
                            getOptionLabel={option => option.label}
                            getOptionDisabled={option => option.disabled}
                            value={opcoesControleEstoque.find(o => o.value === produtoAtual.controleEstoque) ?? opcoesControleEstoque[0]}
                            onChange={(_, newValue) => setChildProduto({ controleEstoque: newValue ? newValue.value : undefined })}
                            renderInput={params => <TextField {...params} label="Tipo" />}
                            fullWidth
                        />
                        {produtoAtual.controleEstoque === TipoControleEstoque.completo && (
                            <FichaTecnica
                                adicionarInsumo={adicionarInsumoChild}
                                excluirInsumo={excluirInsumoChild}
                                fichaTecnica={produtoAtual.fichaTecnica}
                            />
                        )}
                    </Box>
                )}

                {/* ── FISCAL ── */}
                {slotTab === TAB_FISCAL && (
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Imposto (fiscal)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            O NCM deste produto é o que aparece na nota fiscal de cada item do combo.
                        </Typography>
                        <FormularioImposto
                            imposto={produtoAtual.imposto}
                            onChange={(imp) => setChildProduto({ imposto: imp })}
                        />
                    </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button variant="outlined" onClick={voltarParaSlots}>Voltar à lista</Button>
                </Box>

                <ModalAtualizaPreco
                    aberto={atualizacaoPreco}
                    fechar={() => setAtualizacaoPreco(false)}
                    atualizar={handleAtualizarPrecoEmMassa}
                />

                {/* Dialog: criar grupo de complemento (preparado) */}
                <Dialog open={dialogGrupoOpen} onClose={() => setDialogGrupoOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Novo grupo de complemento</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            sx={{ mt: 1 }}
                            label="Nome do grupo (ex.: Sabores)"
                            value={grupoDescricao}
                            onChange={e => setGrupoDescricao(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleCriarGrupo(); }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogGrupoOpen(false)}>Cancelar</Button>
                        <Button variant="contained" onClick={handleCriarGrupo} disabled={!grupoDescricao.trim()}>Criar grupo</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // VIEW: slots (lista de produtos do combo)
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Produtos do combo</Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {isLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Autocomplete
                            options={todosProdutos.filter(p => p.tipo !== TipoProduto.combo)}
                            getOptionLabel={p => `${p.descricao} (${labelTipo(p.tipo)})`}
                            value={produtoSelecionado}
                            onChange={(_, value) => handleSelecionarProduto(value)}
                            renderInput={params => <TextField {...params} label="Copiar produto existente" placeholder="Digite o nome do produto..." />}
                            noOptionsText="Nenhum produto encontrado"
                            fullWidth
                        />
                    )}
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        sx={{ whiteSpace: "nowrap", height: 56 }}
                        onClick={() => { setNovoDescricao(""); setDialogNovoOpen(true); }}
                    >
                        Novo
                    </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 420 }}>
                    <Toolbar sx={{ justifyContent: "space-between" }}>
                        <Typography variant="subtitle1">Itens do combo</Typography>
                        <Box>
                            <Button
                                variant="contained"
                                endIcon={<ArrowDropDown />}
                                disabled={slotsSelecionados.length === 0}
                                onClick={e => setAnchorSlots(e.currentTarget)}
                            >
                                Ações
                            </Button>
                            <Menu anchorEl={anchorSlots} open={Boolean(anchorSlots)} onClose={() => setAnchorSlots(null)}>
                                <MenuItem disabled>
                                    <Typography variant="inherit" noWrap>{slotsSelecionados.length} selecionado(s)</Typography>
                                </MenuItem>
                                <Divider />
                                {slotsSelecionados.length === 1 && (
                                    <MenuItem onClick={() => { irParaSlotItems(slotsSelecionados[0]); setAnchorSlots(null); }}>
                                        <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit">Editar produto</Typography>
                                    </MenuItem>
                                )}
                                <MenuItem onClick={handleExcluirSlotsSelecionados}>
                                    <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                                    <Typography variant="inherit">Excluir</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>

                    {itensCombo.length ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <input type="checkbox"
                                            checked={slotsSelecionados.length === itensCombo.length && itensCombo.length > 0}
                                            onChange={toggleTodosSlots}
                                        />
                                    </TableCell>
                                    <TableCell align="center">Ordem</TableCell>
                                    <TableCell>Produto</TableCell>
                                    <TableCell align="center">Qtd</TableCell>
                                    <TableCell align="center">Preço no combo</TableCell>
                                    <TableCell align="center">Editar</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {itensCombo.map((item, index) => (
                                    <TableRow key={item.uuid} hover>
                                        <TableCell padding="checkbox">
                                            <input type="checkbox" checked={slotsSelecionados.includes(item.uuid)} onChange={() => toggleSlot(item.uuid)} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title="Mover para cima">
                                                <span>
                                                    <IconButton size="small" disabled={index === 0} onClick={e => { e.stopPropagation(); trocarPosicaoItemCombo(index, "cima"); }}>
                                                        <ArrowUpward fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Mover para baixo">
                                                <span>
                                                    <IconButton size="small" disabled={index === itensCombo.length - 1} onClick={e => { e.stopPropagation(); trocarPosicaoItemCombo(index, "baixo"); }}>
                                                        <ArrowDownward fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography fontSize={14}>{item.produto.descricao}</Typography>
                                                <Chip label={labelTipo(item.produto.tipo)} size="small" variant="outlined" />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                size="small"
                                                sx={{ width: 70 }}
                                                value={item.quantidade}
                                                inputProps={{ min: 1 }}
                                                onClick={e => e.stopPropagation()}
                                                onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v > 0) editarItemCombo(item.uuid, { quantidade: v }); }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                sx={{ width: 130 }}
                                                defaultValue={String(item.preco)}
                                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                                onClick={e => e.stopPropagation()}
                                                onBlur={e => { const v = number(e.target.value); if (!isNaN(v)) editarItemCombo(item.uuid, { preco: v }); }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar produto (dados, KDS, estoque)">
                                                <IconButton size="small" onClick={() => irParaSlotItems(item.uuid)}>
                                                    <Settings fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
                            <Typography color="text.secondary">Nenhum produto adicionado ao combo ainda.</Typography>
                        </Box>
                    )}
                </TableContainer>

                <ButtonsContainerForm sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={handleVoltar}>Voltar</Button>
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                        Salve o combo pelo botão "Salvar" no resumo ao lado.
                    </Typography>
                </ButtonsContainerForm>
            </Paper>

            {/* Dialog: preço do item (copiar) */}
            <Dialog open={dialogPrecoOpen} onClose={() => setDialogPrecoOpen(false)}>
                <DialogTitle>Preço de {pendingProduto?.descricao}</DialogTitle>
                <DialogContent>
                    <Typography>Deseja incluir o preço original ({real(pendingProduto?.preco)}) no valor do combo?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Selecione <b>Não</b> para combo com preço fixo (recomendado). Você poderá editar manualmente depois.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogPrecoConfirm(false)}>Não (R$ 0,00)</Button>
                    <Button onClick={() => handleDialogPrecoConfirm(true)} variant="contained">Sim ({real(pendingProduto?.preco)})</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: preços dos complementos (pizza/preparado) */}
            <Dialog open={dialogComplementoOpen} onClose={() => setDialogComplementoOpen(false)}>
                <DialogTitle>Complementos de {pendingProduto?.descricao}</DialogTitle>
                <DialogContent>
                    <Typography>
                        Deseja herdar os preços dos complementos (sabores, bordas, etc.) de <b>{pendingProduto?.descricao}</b>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Selecione <b>Não</b> para zerar todos os preços dos itens. Você poderá editar individualmente depois.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogComplementoConfirm(false)}>Não (zerar preços)</Button>
                    <Button onClick={() => handleDialogComplementoConfirm(true)} variant="contained">Sim (herdar preços)</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: criar produto novo */}
            <Dialog open={dialogNovoOpen} onClose={() => setDialogNovoOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Criar produto novo</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Cria um produto exclusivo deste combo (não aparece no cardápio). Depois configure KDS e estoque na edição do item.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Nome do produto"
                        value={novoDescricao}
                        onChange={e => setNovoDescricao(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleCriarProdutoNovo(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogNovoOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCriarProdutoNovo} disabled={!novoDescricao.trim()}>Criar e editar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
